import type { CuratedItem as WardenclyffeCuratedItem } from "~types/wardenclyffe";

import {
    getAudioImageData,
    getAuthorImageData,
    getCarouselData,
    getDefaultImageData,
    getEmbeddedIframeData,
    getLivestreamVideoData,
    getLoopingVideoData,
    getMagazineCoverImageData,
    getNewsletterImageData,
    getItemOptOutDecision,
    getSnapshotChartData,
    getVerticalVideoData,
    getVideoImageData,
    getVodVideoData,
    stripUndefinedKeys,
} from "./internal";
import type { HydratedMediaSourceData, HydratedDataObject } from "./types";

/**
 * Wardenclyffe Media Transform
 *
 * This transform takes in a WardenclyffeCuratedItem
 * and applies each specific Media Component Getter to
 * determine if we have valid data to support that media
 * format.
 *
 * IMPORTANT CONTRACT:
 * If we have valid data, the key will be added onto
 * HydratedMediaSourceData, if we DO NOT have valid data,
 * the key will be undefined. We will not default to
 * empty string, for example. The key will not be
 * on the object. You can code based on this contract
 * when using the wardenclyffeMediaTransform in your app.
 */
export function wardenclyffeMediaTransform(
    data: WardenclyffeCuratedItem | null
) {
    if (!data) return null;

    const hydratedMediaSourceData = stripUndefinedKeys({
        audioImage: getAudioImageData(data),
        authorImage: getAuthorImageData(data),
        carousel: getCarouselData(data),
        defaultImage: getDefaultImageData(data),
        embeddedIframe: getEmbeddedIframeData(data),
        livestreamVideo: getLivestreamVideoData(data),
        loopingVideo: getLoopingVideoData(data),
        magazineCoverImage: getMagazineCoverImageData(data),
        newsletterImage: getNewsletterImageData(data),
        optOut: getItemOptOutDecision(data),
        snapshotChart: getSnapshotChartData(data),
        verticalVideo: getVerticalVideoData(data),
        videoImage: getVideoImageData(data),
        vodVideo: getVodVideoData(data),
    } as HydratedDataObject<HydratedMediaSourceData>);

    if (Object.keys(hydratedMediaSourceData).length === 0) return null;

    return {
        data: hydratedMediaSourceData,
    };
}
